// Real wallet connectors: Phantom (Solana) + EVM (WalletConnect QR / injected).
// Falls back gracefully; the app still supports the simulated demo wallet.
import api from "@/lib/api";

const b64 = (bytes) => btoa(String.fromCharCode(...bytes));

let _config = null;
export async function getConfig() {
  if (_config) return _config;
  try {
    const { data } = await api.get("/system/config");
    _config = data;
  } catch {
    _config = { walletconnect_project_id: "", evm_chain_id: 4663 };
  }
  return _config;
}

export const getPhantom = () =>
  (typeof window !== "undefined" && (window.phantom?.solana || window.solana)) || null;
export const getInjectedEvm = () =>
  (typeof window !== "undefined" && window.ethereum) || null;
export const hasPhantom = () => !!getPhantom()?.isPhantom;
export const hasEvm = () => !!getInjectedEvm();

let _wcProvider = null;
let _evmProvider = null;

async function getWalletConnectProvider() {
  if (_wcProvider) return _wcProvider;
  const cfg = await getConfig();
  if (!cfg.walletconnect_project_id) return null;
  const { EthereumProvider } = await import("@walletconnect/ethereum-provider");
  const chainId = cfg.evm_chain_id || 4663;
  _wcProvider = await EthereumProvider.init({
    projectId: cfg.walletconnect_project_id,
    chains: [chainId],
    optionalChains: [chainId, 1],
    showQrModal: true,
    metadata: {
      name: "Wen Moon",
      description: "Launch on any pad. Run the campaign. Feed the burn.",
      url: typeof window !== "undefined" ? window.location.origin : "",
      icons: [],
    },
  });
  return _wcProvider;
}

// Connect Phantom and sign a nonce (SIWS). Returns the user object from the backend.
export async function connectPhantomAndSignIn() {
  const provider = getPhantom();
  if (!provider) {
    window.open("https://phantom.app/", "_blank");
    throw new Error("Phantom wallet not found. Install it and try again.");
  }
  const resp = await provider.connect();
  const wallet = resp.publicKey.toString();
  const { data } = await api.post("/auth/nonce", { wallet });
  const encoded = new TextEncoder().encode(data.message);
  const signed = await provider.signMessage(encoded, "utf8");
  const signature = b64(signed.signature ?? signed);
  const { data: user } = await api.post("/auth/verify", { wallet, nonce: data.nonce, signature });
  return { wallet, user };
}

// Connect an EVM wallet (WalletConnect QR if configured, else injected) and personal_sign a nonce.
export async function connectEvmAndSignIn() {
  const cfg = await getConfig();
  let provider = null;
  let address = null;
  if (cfg.walletconnect_project_id) {
    provider = await getWalletConnectProvider();
    const accounts = await provider.enable(); // opens QR modal
    address = accounts[0];
  } else {
    provider = getInjectedEvm();
    if (!provider) throw new Error("No EVM wallet found. Install MetaMask, or ask the admin to enable WalletConnect (mobile QR).");
    [address] = await provider.request({ method: "eth_requestAccounts" });
  }
  _evmProvider = provider;
  const { data } = await api.post("/auth/evm/nonce", { address });
  const signature = await provider.request({ method: "personal_sign", params: [data.message, address] });
  const { data: user } = await api.post("/auth/evm/verify", { address, nonce: data.nonce, signature });
  return { wallet: address.toLowerCase(), user };
}

async function payEvm(intent) {
  const provider = _evmProvider || getInjectedEvm();
  if (!provider) throw new Error("EVM wallet required. Reconnect with WalletConnect or MetaMask.");
  let from;
  try { [from] = await provider.request({ method: "eth_accounts" }); } catch { /* noop */ }
  if (!from) [from] = await provider.request({ method: "eth_requestAccounts" });
  const chainHex = "0x" + Number(intent.chain_id).toString(16);
  try {
    await provider.request({ method: "wallet_switchEthereumChain", params: [{ chainId: chainHex }] });
  } catch { /* chain may not be added; proceed */ }
  const signature = await provider.request({
    method: "eth_sendTransaction",
    params: [{ from, to: intent.to, value: intent.value_wei_hex }],
  });
  return signature;
}

async function paySolana(intent) {
  const provider = getPhantom();
  if (!provider) throw new Error("Phantom wallet required for on-chain payment.");
  const { Transaction } = await import("@solana/web3.js");
  const txBytes = Uint8Array.from(atob(intent.transaction), (c) => c.charCodeAt(0));
  const tx = Transaction.from(txBytes);
  const { signature } = await provider.signAndSendTransaction(tx);
  return signature;
}

// On-chain crypto checkout: build intent, sign+send with the right wallet, confirm.
export async function payOnChain(payload) {
  const { data: intent } = await api.post("/billing/checkout/intent", payload);
  const signature = intent.network === "evm" ? await payEvm(intent) : await paySolana(intent);
  let lastErr;
  for (let i = 0; i < 8; i++) {
    try {
      const { data } = await api.post("/billing/checkout/confirm", { intent_id: intent.intent_id, signature });
      return data;
    } catch (e) {
      lastErr = e;
      await new Promise((r) => setTimeout(r, 2500));
    }
  }
  throw lastErr || new Error("Could not confirm payment.");
}

// One-click Pump.fun launch (non-custodial): generate mint, upload metadata, sign create tx with mint + Phantom.
export async function launchOnPumpfun(launchId) {
  const status = (await api.get("/launch/pumpfun/status")).data;
  if (!status.enabled) {
    const { data } = await api.post(`/launch/${launchId}/pumpfun/prepare`, { mint: "11111111111111111111111111111111" });
    return { mode: "simulated", message: data.message };
  }
  const provider = getPhantom();
  if (!provider) throw new Error("Connect Phantom to launch on Pump.fun.");
  await provider.connect();
  const { Keypair, VersionedTransaction } = await import("@solana/web3.js");
  const mint = Keypair.generate(); // secret stays in this browser only
  const meta = (await api.post(`/launch/${launchId}/pumpfun/metadata`)).data;
  const { data: prep } = await api.post(`/launch/${launchId}/pumpfun/prepare`, {
    mint: mint.publicKey.toBase58(), uri: meta.metadataUri, amount: 0, slippage: 10, priorityFee: 0.00001,
  });
  if (prep.mode === "simulated") return prep;
  const raw = Uint8Array.from(atob(prep.transactionBase64), (c) => c.charCodeAt(0));
  const tx = VersionedTransaction.deserialize(raw);
  tx.sign([mint]); // mint partial-sign; Phantom signs creator/fee-payer and submits
  const { signature } = await provider.signAndSendTransaction(tx);
  return { mode: "live", mint: mint.publicKey.toBase58(), signature,
           explorer_url: `https://solscan.io/tx/${signature}` };
}
